import { createSelector } from '@reduxjs/toolkit';

import { IesCandelaGrid } from '../../types/lampWireframes.ts';
import { Version } from '../../types/project.ts';
import { RootState } from '../store.ts';

export interface LampWireframeMapEntry {
  wireframe?: { url: string; rotation: number; iesGrid?: IesCandelaGrid };
  wireframeLoading?: boolean;
}

export const selectLampWireframeMap = createSelector(
  [
    (state: RootState) => state.workspace.project?.activeVersionId,
    (state: RootState) => state.workspace.lampWireframes,
  ],
  (activeVersionId, lampWireframes) => {
    const map = new Map<string, LampWireframeMapEntry>();
    const thirdPartyLampUuids = new Set((lampWireframes.thirdPartyWireframes ?? []).map((f) => f.lampUuid));

    // Taman wireframes (from app IES files) - only when they match the active version
    if (lampWireframes.versionId === activeVersionId) {
      for (const file of lampWireframes.tamanWireframes) {
        if (!thirdPartyLampUuids.has(file.lampUuid)) {
          map.set(file.lampUuid, {
            wireframe: { url: file.url, rotation: 0, iesGrid: file.iesGrid },
          });
        }
      }
    }

    // Third-party wireframes (from lamp similarity) override for those lamps
    for (const entry of lampWireframes.thirdPartyWireframes ?? []) {
      if (!entry.url) {
        map.set(entry.lampUuid, { wireframeLoading: true });
      } else {
        map.set(entry.lampUuid, {
          wireframe: { url: entry.url, rotation: entry.rotationRadians ?? 0, iesGrid: entry.iesGrid },
        });
      }
    }
    return map;
  },
);

/** The version open in the workspace (same lookup as `useStore().activeVersion`, without
 *  subscribing to the whole workspace slice). */
export const selectActiveVersion = (state: RootState): Version | undefined => {
  const project = state.workspace.project;
  if (!project || !project.activeVersionId || !project.versions) return undefined;
  return project.versions.find((v) => v.id === project.activeVersionId);
};
